/**
 * Shared visual primitives for diff-style modals
 * (ReplacementDiffModal, BulkDiffModal, etc).
 *
 * Centralizes the modal layout shells, the 3-column diff grid
 * (Name | Old value | New value), the "N changes" pill, and the footer
 * styling so the two modals stay visually in sync without duplicating
 * styled-component definitions.
 */

import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Modal layout shells
// ---------------------------------------------------------------------------

export const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  maxHeight: "calc(90vh - 96px)",
  minHeight: 0,
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const ModalBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));

// ---------------------------------------------------------------------------
// 3-column diff grid (Name | Old value | New value)
// ---------------------------------------------------------------------------

export const TableHeader = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: "16px",
  padding: "8px 12px",
  color: "#666",
  fontSize: "13px",
  fontWeight: 600,
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: "#fff",
});

export const TableRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: "16px",
  padding: "10px 12px",
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: "#fff",
  fontSize: "14px",
  color: "#333",
  "&:last-child": {
    borderBottom: "none",
  },
});

export const OldValue = styled("span")({
  color: "#999",
  textDecoration: "line-through",
});

// ---------------------------------------------------------------------------
// Section title row + change-count pill
// ---------------------------------------------------------------------------

export const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const ChangePill = styled("span")({
  backgroundColor: "#90A4AE",
  color: "#fff",
  borderRadius: "999px",
  padding: "2px 8px",
  fontSize: "12px",
  fontWeight: 600,
});

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(1),
  borderTop: "1px solid #e0e0e0",
}));
